import { loadWebAnalytics } from '../config/firebase.web';
import type { BudgetUser } from '../types/auth';
import { PRO_PRICE_USD } from './billingService';
import {
  getTelemetryPlatform,
  toTelemetryUser,
  type CheckoutAbandonResult,
  type PaywallDismissReason,
  type PaywallSource,
  type TelemetryFeature,
  type TelemetryLoginMethod,
  type TelemetryTab,
} from './telemetryTypes';

function ctx(params: Record<string, string | number | undefined> = {}) {
  return { platform: getTelemetryPlatform(), ...params };
}

const PRO_ITEM = {
  item_id: 'pro',
  item_name: 'MoneyLens Pro',
  item_category: 'subscription',
} as const;

let lastCheckoutSource: PaywallSource | undefined;

async function safe(label: string, work: () => Promise<void>): Promise<void> {
  try {
    await work();
  } catch (error) {
    console.warn(`[telemetry] ${label} failed`, error);
  }
}

type IdleWindow = {
  document?: { readyState: string };
  addEventListener?: (type: string, listener: () => void, options?: { once?: boolean }) => void;
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
};

/** Wait until `window.load` and an idle slot so the Firebase chunk stays off first paint. */
function whenIdleAfterLoad(): Promise<void> {
  return new Promise((resolve) => {
    const win = globalThis as IdleWindow;
    const idle = () => {
      if (typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(() => resolve(), { timeout: 3500 });
      } else {
        setTimeout(resolve, 1);
      }
    };
    if (win.document?.readyState === 'complete') {
      idle();
    } else if (typeof win.addEventListener === 'function') {
      win.addEventListener('load', idle, { once: true });
    } else {
      idle();
    }
  });
}

export const telemetry = {
  bootstrap(): Promise<void> {
    return safe('bootstrap', async () => {
      await whenIdleAfterLoad();
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.setUserProperties(analytics, { platform: getTelemetryPlatform() });
      api.logEvent(analytics, 'app_open');
      console.log('[telemetry] Analytics ready (web)');
    });
  },

  identify(user: BudgetUser): Promise<void> {
    return safe('identify', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      const snapshot = toTelemetryUser(user);
      api.setUserId(analytics, snapshot.id);
      api.setUserProperties(analytics, {
        logged_in: 'true',
        plan: snapshot.plan,
        is_paid: snapshot.isPaid ? 'true' : 'false',
        provider: snapshot.provider,
        sub_status: snapshot.subscriptionStatus.slice(0, 36),
        platform: getTelemetryPlatform(),
      });
      console.log('[telemetry] identified user', {
        userId: snapshot.id,
        plan: snapshot.plan,
        isPaid: snapshot.isPaid,
      });
    });
  },

  clearUser(): Promise<void> {
    return safe('clearUser', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.setUserId(analytics, null);
      api.setUserProperties(analytics, {
        logged_in: 'false',
        plan: 'none',
        is_paid: 'false',
        provider: 'none',
        sub_status: 'none',
      });
      console.log('[telemetry] cleared user');
    });
  },

  logLogin(method: TelemetryLoginMethod = 'unknown'): Promise<void> {
    return safe('logLogin', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'login', { method });
      console.log('[telemetry] login', { method });
    });
  },

  logLogout(): Promise<void> {
    return safe('logLogout', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'logout');
      console.log('[telemetry] logout');
    });
  },

  logScreenView(screenName: string, tab?: string): Promise<void> {
    return safe('logScreenView', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: tab ?? screenName,
      });
      api.logEvent(analytics, 'screen_viewed', ctx({
        screen_name: screenName,
        tab: tab ?? 'none',
      }));
      console.log('[telemetry] screen', { screenName, tab });
    });
  },

  logTabView(tab: TelemetryTab): Promise<void> {
    return safe('logTabView', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'tab_view', ctx({ tab }));
      console.log('[telemetry] tab_view', { tab });
    });
  },

  logFeature(feature: TelemetryFeature, extra?: Record<string, string | number>): Promise<void> {
    return safe('logFeature', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'feature_use', ctx({ feature, ...extra }));
      console.log('[telemetry] feature_use', { feature, ...extra });
    });
  },

  logUpgradeEntry(source: PaywallSource): Promise<void> {
    return safe('logUpgradeEntry', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'upgrade_entry', ctx({ source }));
      console.log('[telemetry] upgrade_entry', { source });
    });
  },

  logPaywallView(source: PaywallSource): Promise<void> {
    return safe('logPaywallView', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'paywall_view', ctx({ source, plan: 'pro' }));
      console.log('[telemetry] paywall_view', { source });
    });
  },

  logUpgradeCtaClick(source: PaywallSource): Promise<void> {
    return safe('logUpgradeCtaClick', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'upgrade_cta_click', ctx({ source, plan: 'pro' }));
      console.log('[telemetry] upgrade_cta_click', { source });
    });
  },

  logPaywallDismiss(source: PaywallSource, reason: PaywallDismissReason): Promise<void> {
    return safe('logPaywallDismiss', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'paywall_dismiss', ctx({ source, reason }));
      console.log('[telemetry] paywall_dismiss', { source, reason });
    });
  },

  logBeginCheckout(source: PaywallSource): Promise<void> {
    lastCheckoutSource = source;
    return safe('logBeginCheckout', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'begin_checkout', {
        ...ctx({ source, plan: 'pro' }),
        value: PRO_PRICE_USD,
        currency: 'USD',
        items: [PRO_ITEM],
      });
      console.log('[telemetry] begin_checkout', { source });
    });
  },

  logCheckoutAbandon(source: PaywallSource, result: CheckoutAbandonResult): Promise<void> {
    return safe('logCheckoutAbandon', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'checkout_abandon', ctx({ source, result }));
      console.log('[telemetry] checkout_abandon', { source, result });
    });
  },

  logSubscribePro(source?: PaywallSource): Promise<void> {
    const origin = source ?? lastCheckoutSource;
    lastCheckoutSource = undefined;
    return safe('logSubscribePro', async () => {
      const loaded = await loadWebAnalytics();
      if (!loaded) return;
      const { analytics, api } = loaded;
      api.logEvent(analytics, 'subscribe_pro', {
        ...ctx({ plan: 'pro', is_paid: 'true' }),
        ...(origin ? { source: origin } : {}),
      });
      api.logEvent(analytics, 'purchase', {
        value: PRO_PRICE_USD,
        currency: 'USD',
        transaction_id: `pro_${Date.now()}`,
        items: [PRO_ITEM],
      });
      console.log('[telemetry] subscribe_pro', { source: origin });
    });
  },

  recordError(error: Error, context?: Record<string, string>): Promise<void> {
    return safe('recordError', async () => {
      console.warn('[telemetry] web error (Crashlytics is native-only)', error.message, context);
    });
  },
};
